// src/components/practice/tasks/writing/PaperExamStyle.jsx - REFACTORED VERSION
import React from 'react';
import { marked } from 'marked';
import WritingWordCounter from '../writing/WritingWordCounter.jsx';
import { ArrowLeft, CheckSquare } from 'lucide-react';

// Import layout components
import StandardLayout from './layouts/StandardLayout.jsx';
import GoetheFormalMessageLayout from './layouts/GoetheFormalMessageLayout.jsx';
import OsdB1EmailLayout from './layouts/OsdB1EmailLayout.jsx';
import TelcB2FormalLetterLayout from './layouts/TelcB2FormalLetterLayout.jsx';
import OsdB2FlyerLayout from './layouts/OsdB2FlyerLayout.jsx';
import OsdB2EssayLayout from './layouts/OsdB2EssayLayout.jsx';
import OsdC1EmailLayout from './layouts/OsdC1EmailLayout.jsx';

const PaperExamStyle = ({ partData, userText, handleTextChange, onBackToSelection, images = [] }) => {
    const task = partData.task || {};
    const {
        guidingPoints = [],
        scenario,
        inputContent,
        wordLimit,
        requirements = []
    } = task;

    const provider = partData.examProvider;
    const effectiveWordLimit = wordLimit || partData.wordLimit;

    // Detect special task types
    const isFormalMessage = partData.taskType === 'formal-message';
    const isOsdB1Email = partData.examProvider?.toUpperCase() === 'OSD' &&
                         partData.level?.toUpperCase() === 'B1' &&
                         partData.taskType === 'email';

    // OSD B2 formal-letter
    const isOsdB2FormalLetter = partData.examProvider?.toUpperCase() === 'OSD' &&
                                partData.level?.toUpperCase() === 'B2' &&
                                partData.taskType === 'formal-letter';

    // TELC B2 formal-letter (same layout as OSD)
    const isTelcB2FormalLetter = partData.examProvider?.toUpperCase() === 'TELC' &&
                                 partData.level?.toUpperCase() === 'B2' &&
                                 partData.taskType === 'formal-letter';

    // Combined check for B2 formal-letter with advertisement layout
    const isB2FormalLetterWithAd = (isOsdB2FormalLetter || isTelcB2FormalLetter) &&
                                    task.inputType === 'advertisement';

    const isOsdB2Essay = partData.examProvider?.toUpperCase() === 'OSD' &&
                         partData.level?.toUpperCase() === 'B2' &&
                         partData.taskType === 'choice-writing-essay';
    const isOsdC1FormalResponse = partData.examProvider?.toUpperCase() === 'OSD' &&
                                  partData.level?.toUpperCase() === 'C1' &&
                                  partData.taskType === 'formal-response-email';

    return (
        <div className="max-w-4xl mx-auto font-serif">
            {onBackToSelection && (
                <button
                    onClick={onBackToSelection}
                    className="mb-4 inline-flex items-center px-3 py-1.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group"
                >
                    <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
                    Andere Aufgabe wählen
                </button>
            )}

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-10 border border-gray-200">
                {/* Header */}
                <header className="text-center mb-8 border-b pb-4">
                    <h1 className="text-2xl font-bold text-gray-800 mb-1">
                        {partData.examName || `${provider} Zertifikat ${partData.level}`}
                    </h1>
                    <h2 className="text-lg text-gray-600 font-medium">
                        {partData.partName || "Schriftlicher Ausdruck"}
                    </h2>
                </header>

                {/* Instructions */}
                {partData.instructions && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-md prose prose-sm max-w-none"
                         dangerouslySetInnerHTML={{ __html: marked.parse(partData.instructions) }} />
                )}

                {/* Image Display */}
                {images && images.length > 0 && (
                    <div className="w-full flex justify-center my-6">
                        {images.map((imagePath, index) => (
                            <img
                                key={index}
                                src={imagePath}
                                alt={`Writing task ${index + 1}`}
                                className="max-w-full h-auto rounded-md shadow-md"
                                style={{ maxHeight: '400px' }}
                            />
                        ))}
                    </div>
                )}

                {/* Scenario - Regular display */}
                {scenario && !isB2FormalLetterWithAd && !isOsdB2FormalLetter && !isTelcB2FormalLetter && !isOsdB2Essay && !isOsdC1FormalResponse && (
                    <div className="mb-6">
                        <p className="text-gray-800 leading-relaxed prose max-w-none"
                           dangerouslySetInnerHTML={{ __html: marked.parse(scenario) }} />
                    </div>
                )}

                {/* Scenario - B2 Formal Letter with Advertisement (OSD/TELC) */}
                {isB2FormalLetterWithAd && (
                    <TelcB2FormalLetterLayout
                        scenario={scenario}
                        inputContent={inputContent}
                        guidingPoints={guidingPoints}
                        task={task}
                    />
                )}

                {/* Scenario - OSD B2 Formal Letter (just show situation) - for flyer style */}
                {scenario && (isOsdB2FormalLetter || isTelcB2FormalLetter) && !isB2FormalLetterWithAd && (
                    <div className="mb-4">
                        <p className="text-gray-800 leading-relaxed">
                            <strong>Situation:</strong> {scenario.split('\n\n')[0]}
                        </p>
                    </div>
                )}

                {/* Scenario - OSD B2 Essay Layout */}
                {scenario && isOsdB2Essay && (
                    <OsdB2EssayLayout scenario={scenario} />
                )}

                {/* Input Content - Regular display */}
                {inputContent && !isOsdB1Email && !isB2FormalLetterWithAd && !isOsdB2FormalLetter && !isTelcB2FormalLetter && !isOsdB2Essay && !isOsdC1FormalResponse && (
                    <div className="border border-gray-300 p-4 rounded-md my-6 bg-gray-50/50">
                        <div className="prose prose-sm max-w-none"
                             dangerouslySetInnerHTML={{ __html: marked.parse(inputContent) }} />
                    </div>
                )}

                {/* Input Content - OSD B1 Email Layout */}
                {inputContent && isOsdB1Email && (
                    <OsdB1EmailLayout
                        inputContent={inputContent}
                        task={task}
                        guidingPoints={guidingPoints}
                    />
                )}

                {/* Input Content - OSD B2 Flyer Layout (for flyer-style tasks) */}
                {inputContent && (isOsdB2FormalLetter || isTelcB2FormalLetter) && !isB2FormalLetterWithAd && (
                    <OsdB2FlyerLayout
                        inputContent={inputContent}
                        scenario={scenario}
                    />
                )}

                {/* Input Content - OSD C1 Email Layout */}
                {inputContent && isOsdC1FormalResponse && (
                    <OsdC1EmailLayout
                        inputContent={inputContent}
                        scenario={scenario}
                    />
                )}

                {/* Requirements */}
                {requirements.length > 0 && (
                    <div className="my-6 p-4 bg-gray-50 rounded-md border-l-4 border-gray-400">
                        <p className="font-semibold text-gray-800 mb-3 flex items-center">
                            <CheckSquare className="w-5 h-5 mr-2 text-gray-600" />
                            Anforderungen:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 prose-sm">
                            {requirements.map((req, index) => (
                                <li key={index}>{req}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Guiding Points - Standard Layout (only if not special format) */}
                {guidingPoints.length > 0 && !isFormalMessage && !isOsdB1Email && !isB2FormalLetterWithAd && !isOsdB2FormalLetter && !isTelcB2FormalLetter && !isOsdB2Essay && !isOsdC1FormalResponse && (
                    <div className="my-6">
                        <p className="font-semibold text-gray-800 mb-3">
                            Behandeln Sie in Ihrem Text die folgenden Punkte:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 prose-sm">
                            {guidingPoints.map((point, index) => (
                                <li key={index}>{point.text || point}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Guiding Points - Goethe B2 Formal Message Layout */}
                {guidingPoints.length > 0 && isFormalMessage && (
                    <GoetheFormalMessageLayout
                        guidingPoints={guidingPoints}
                        effectiveWordLimit={effectiveWordLimit}
                    />
                )}

                {/* Word Counter */}
                <div className="my-6">
                    <WritingWordCounter text={userText} wordLimit={effectiveWordLimit} />
                </div>

                {/* Text Area */}
                <div className="mt-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ihre Antwort:
                    </label>
                    <textarea
                        value={userText}
                        onChange={handleTextChange}
                        placeholder="Schreiben Sie Ihren Text hier..."
                        className="w-full h-96 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y text-base leading-relaxed bg-gray-50/30"
                    />
                </div>
            </div>
        </div>
    );
};

export default PaperExamStyle;